/**
 * SalesRecipesPortal — `hustleplate.au/recipes`
 * ─────────────────────────────────────────────
 * Static SEO portal that ships with the salespage. Three goals:
 *
 *  1. Browseable by **search-engine traffic** without any login —
 *     every recipe gets a public URL with its own OG tags.
 *  2. **Client-side fuzzy search** across all 2,400 recipes (name +
 *     ingredients + cuisine + hashtags) using Fuse.js so search is
 *     instant and the portal can be served from a static CDN.
 *  3. Every "do more" action (print, save, upload your own photo,
 *     add to my cookbook) **funnels to `hustlerecipes.au`** — the
 *     member-gated app. The static portal itself is read-only.
 *
 * Layout
 *   • Hero with title + recipe count
 *   • Sticky filter bar — fuzzy search input + cuisine + course + dietary
 *   • Grid of recipe cards (image, name, cuisine·course pill, intro)
 *   • Footer band that explains the upgrade ("Print + save on
 *     hustlerecipes.au")
 *
 * Why no server search?
 *   Static-CDN target. 2,400 rows × ~250 bytes each ≈ 600 KB, gzipped
 *   ~120 KB — well under the budget for a hero-fold page. Loading the
 *   whole index once means every keystroke is local and zero-latency.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Fuse from "fuse.js";
import { Search, ChefHat, Clock, ArrowRight, ExternalLink, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import SalesNav from "./SalesNav";
import "./SalesPage.css";

// Where the member-gated app lives. We never expose member features
// on the SEO portal — every "do more" CTA bounces here.
const MEMBER_HOST = "https://hustlerecipes.au";

const COURSE_LABELS = {
  starter: "Starter", main: "Main", soup: "Soup",
  dessert: "Dessert", tapas: "Tapas", side: "Side",
  sides: "Sides", breakfast: "Breakfast", baking: "Baking",
  sauces: "Sauces", drinks: "Drinks",
};

const CUISINE_LABELS = {
  italian: "Italian", french: "French", chinese: "Chinese",
  japanese: "Japanese", indian: "Indian", thai: "Thai",
  vietnamese: "Vietnamese", spanish: "Spanish", lebanese: "Lebanese",
  mexican: "Mexican", greek: "Greek", modern_aus: "Modern Australian",
  korean: "Korean", moroccan: "Moroccan", turkish: "Turkish",
  peruvian: "Peruvian", british: "British", american_bbq: "American BBQ",
  caribbean: "Caribbean", filipino: "Filipino", polish: "Polish",
  russian: "Russian", nepalese: "Nepalese", sudanese: "Sudanese",
  seafood: "Seafood", bbq: "BBQ", ethiopian: "Ethiopian",
};

const DIET_LABELS = {
  vegan: "Vegan", vegetarian: "Vegetarian",
  gf: "Gluten free", gluten_free: "Gluten free",
  df: "Dairy free", dairy_free: "Dairy free",
  nut_free: "Nut free", "nut-free": "Nut free",
  pescatarian: "Pescatarian", halal: "Halal", kosher: "Kosher",
  low_carb: "Low carb", "low-carb": "Low carb",
  keto: "Keto", paleo: "Paleo", whole30: "Whole 30",
  sugar_free: "Sugar free", egg_free: "Egg free",
  soy_free: "Soy free", contains_alcohol: "Contains alcohol",
};

function prettyCuisine(c) { return CUISINE_LABELS[c] || (c ? c[0].toUpperCase() + c.slice(1) : ""); }
function prettyCourse(c) { return COURSE_LABELS[c] || (c ? c[0].toUpperCase() + c.slice(1) : ""); }
function prettyDiet(d) {
  if (!d) return "";
  if (DIET_LABELS[d]) return DIET_LABELS[d];
  // Fallback — turn 'something_like_this' / 'something-like-this' into 'Something like this'
  return d
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

const PAGE_SIZE = 36;

export default function SalesRecipesPortal() {
  const [index, setIndex] = useState({ items: [], cuisines: [], courses: [], dietary: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cuisine, setCuisine] = useState("");
  const [course, setCourse] = useState("");
  const [diet, setDiet] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setLoading(true);
    api.get("/hustle/recipes/index.json")
      .then((r) => setIndex(r.data))
      .catch(() => setIndex({ items: [], cuisines: [], courses: [], dietary: [], count: 0 }))
      .finally(() => setLoading(false));
  }, []);

  // Build the Fuse index once per data load. Weighting reflects what
  // home-cooks actually search by: dish name > main ingredients > cuisine.
  const fuse = useMemo(() => new Fuse(index.items, {
    keys: [
      { name: "title", weight: 0.55 },
      { name: "ingredients", weight: 0.25 },
      { name: "cuisine", weight: 0.08 },
      { name: "hashtags", weight: 0.07 },
      { name: "intro", weight: 0.05 },
    ],
    threshold: 0.35,
    ignoreLocation: true,
    minMatchCharLength: 2,
  }), [index.items]);

  // Filter pipeline: first the structured drop-downs (instant), then
  // fuzzy search if any q was typed.
  const filtered = useMemo(() => {
    let list = index.items;
    if (cuisine) list = list.filter((r) => r.cuisine === cuisine);
    if (course)  list = list.filter((r) => r.course  === course);
    if (diet)    list = list.filter((r) => (r.dietary || []).includes(diet));
    if (q.trim().length >= 2) {
      const results = fuse.search(q.trim());
      const filteredIds = new Set(list.map((r) => r.slug));
      list = results.map((r) => r.item).filter((r) => filteredIds.has(r.slug));
    }
    return list;
  }, [index.items, cuisine, course, diet, q, fuse]);

  useEffect(() => { setVisible(PAGE_SIZE); }, [q, cuisine, course, diet]);

  return (
    <div data-testid="sales-recipes-portal" className="bg-[#FDFBF7] min-h-screen">
      <SalesNav />
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#121614] text-white">
        <div className="absolute inset-0 opacity-25 pointer-events-none"
             style={{
               backgroundImage: "radial-gradient(circle at 25% 35%, #5A7247 0%, transparent 55%)," +
                                "radial-gradient(circle at 78% 60%, #C05746 0%, transparent 55%)",
             }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="inline-flex items-center gap-2 text-[#F9D29B] text-xs uppercase tracking-[0.25em] font-bold mb-4">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} />
            HustlePlate Recipe Library
          </div>
          <h1 className="font-serif-display text-4xl md:text-6xl lg:text-7xl leading-tight">
            {index.count.toLocaleString()} recipes,<br/>twelve cuisines, one library.
          </h1>
          <p className="mt-6 max-w-2xl text-[#F2EBE0]/85 text-lg leading-relaxed">
            Search by dish, ingredient, dietary tag or cuisine.
            Cook from the screen — or sign in at{" "}
            <a
              href={MEMBER_HOST}
              data-testid="hero-member-link"
              className="underline underline-offset-4 hover:text-[#F9D29B]"
            >
              hustlerecipes.au
            </a>{" "}
            to print, save and add your own photos.
          </p>
        </div>
      </section>

      {/* Sticky filter bar */}
      <section className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#E8E5DF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-4 flex items-center gap-2 rounded-xl border border-[#E8E5DF] bg-white px-3 h-11">
            <Search className="h-4 w-4 text-[#5C625E]" />
            <input
              data-testid="recipes-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by dish, ingredient or cuisine…"
              className="flex-1 bg-transparent outline-none text-sm"
            />
            {q && (
              <button
                type="button"
                data-testid="recipes-search-clear"
                onClick={() => setQ("")}
                className="text-xs text-[#5C625E] hover:text-[#C05746]"
              >
                Clear
              </button>
            )}
          </div>
          {/* Single-click reset for cuisine + course + dietary + search */}
          <button
            type="button"
            data-testid="recipes-clear-filters"
            onClick={() => { setQ(""); setCuisine(""); setCourse(""); setDiet(""); }}
            disabled={!q && !cuisine && !course && !diet}
            className="md:col-span-2 h-11 rounded-xl border border-[#E8E5DF] text-sm font-medium text-[#C05746] hover:bg-[#FDF1EE] hover:border-[#C05746] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-[#E8E5DF] transition-colors"
          >
            Clear filters
          </button>
          <select
            data-testid="recipes-cuisine"
            value={cuisine}
            onChange={(e) => setCuisine(e.target.value)}
            className="md:col-span-2 h-11 rounded-xl border border-[#E8E5DF] bg-white text-sm px-3"
          >
            <option value="">All cuisines</option>
            {index.cuisines.map((c) => (
              <option key={c} value={c}>{prettyCuisine(c)}</option>
            ))}
          </select>
          <select
            data-testid="recipes-course"
            value={course}
            onChange={(e) => setCourse(e.target.value)}
            className="md:col-span-2 h-11 rounded-xl border border-[#E8E5DF] bg-white text-sm px-3"
          >
            <option value="">All courses</option>
            {index.courses.map((c) => (
              <option key={c} value={c}>{prettyCourse(c)}</option>
            ))}
          </select>
          <select
            data-testid="recipes-dietary"
            value={diet}
            onChange={(e) => setDiet(e.target.value)}
            className="md:col-span-2 h-11 rounded-xl border border-[#E8E5DF] bg-white text-sm px-3"
          >
            <option value="">Any dietary</option>
            {index.dietary.map((d) => (
              <option key={d} value={d}>{prettyDiet(d)}</option>
            ))}
          </select>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-3 text-xs text-[#5C625E]">
          {loading
            ? "Loading recipe library…"
            : <span data-testid="recipes-result-count">{filtered.length.toLocaleString()} recipes match</span>}
        </div>
      </section>

      {/* Recipe grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {!loading && filtered.length === 0 && (
          <p
            data-testid="recipes-empty"
            className="text-center text-[#5C625E] py-16 italic"
          >
            No recipes match. Try clearing a filter or different keyword.
          </p>
        )}
        <ul
          data-testid="recipes-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        >
          {filtered.slice(0, visible).map((r) => (
            <li
              key={r.slug}
              data-testid={`recipe-card-${r.slug}`}
              className="group bg-white border border-[#E8E5DF] rounded-2xl overflow-hidden hover:shadow-lg transition-shadow"
            >
              <Link to={`/recipes/${r.slug}`} className="block">
                <div className="aspect-[4/3] bg-[#F3F0EA] relative overflow-hidden">
                  {r.image_url ? (
                    <img
                      src={r.image_url}
                      alt={r.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#5C625E]/40">
                      <ChefHat className="h-12 w-12" strokeWidth={1.25} />
                    </div>
                  )}
                  <div className="absolute top-3 left-3 flex items-center gap-1.5">
                    <span className="text-[10px] uppercase tracking-[0.15em] font-bold bg-white/90 backdrop-blur text-[#121614] rounded-full px-2.5 py-1">
                      {prettyCuisine(r.cuisine)}
                    </span>
                    {r.course && (
                      <span className="text-[10px] uppercase tracking-[0.15em] font-bold bg-[#5A7247] text-white rounded-full px-2.5 py-1">
                        {prettyCourse(r.course)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-serif-display text-lg leading-snug group-hover:text-[#C05746] transition-colors">
                    {r.title}
                  </h3>
                  {r.intro && (
                    <p className="mt-1.5 text-sm text-[#5C625E] line-clamp-2">{r.intro}</p>
                  )}
                  {r.source_name && (
                    <div
                      className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#5C625E]"
                      data-testid={`recipe-card-source-${r.slug}`}
                    >
                      {r.source_logo && (
                        <img
                          src={r.source_logo}
                          alt=""
                          className="h-4 w-4 rounded-full object-cover"
                        />
                      )}
                      <span>Credit: {r.source_name}</span>
                    </div>
                  )}
                  <div className="mt-3 flex items-center gap-3 text-[11px] text-[#5C625E]">
                    {r.cook_minutes > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {r.cook_minutes} min
                      </span>
                    )}
                    {(r.dietary || []).slice(0, 2).map((d) => (
                      <span key={d} className="bg-[#F3F0EA] text-[#5A7247] rounded-full px-2 py-0.5">
                        {prettyDiet(d)}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>

        {!loading && filtered.length > visible && (
          <div className="mt-10 flex justify-center">
            <button
              type="button"
              data-testid="recipes-load-more"
              onClick={() => setVisible((v) => v + PAGE_SIZE)}
              className="rounded-full px-6 py-3 border border-[#121614] hover:bg-[#121614] hover:text-white transition-colors text-sm font-medium"
            >
              Show {Math.min(PAGE_SIZE, filtered.length - visible)} more
            </button>
          </div>
        )}
      </section>

      {/* Member upsell footer */}
      <section className="bg-[#121614] text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif-display text-3xl md:text-4xl">
            Want to print, save and add your own photos?
          </h2>
          <p className="mt-4 text-[#F2EBE0]/80 max-w-2xl mx-auto">
            Hustle Members unlock a full cookbook — print-friendly recipe cards, your own photo
            uploads, custom collections and shopping-list export. Free to join.
          </p>
          <a
            href={MEMBER_HOST}
            data-testid="footer-member-cta"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#C05746] hover:bg-[#A64A3C] px-7 py-3.5 text-sm font-bold"
          >
            Visit hustlerecipes.au <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </div>
  );
}
